import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  Box,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  Button,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

import DashboardLayout from "app/layout/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/layout/DashboardNavbar";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";

import ConfigPeajesHeader from "./ConfigPeajesHeader";
import { filterConcesiones, estacionesDe } from "./configurationModel";
import ConcesionCard from "./ConcesionCard";
import ConcesionModal from "./ConcesionModal";
import {
  getConcesionesService,
  updatePoliticaPagoService,
} from "modules/finops/conciliaciones/services/peajesService";

const ConfiguracionPeajes = () => {
  const [concesiones, setConcesiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [openModal, setOpenModal] = useState(false);
  const [concesionSeleccionada, setConcesionSeleccionada] = useState(null);

  const revision = useRef(0);
  const loadData = useCallback(async () => {
    const id = ++revision.current;
    setLoading(true);
    setError(null);
    try {
      const res = await getConcesionesService();
      if (id !== revision.current) return;
      if (res?.success) setConcesiones(Array.isArray(res.data) ? res.data : []);
      else setError(res?.error || "Error al cargar concesiones");
    } catch (e) {
      if (id === revision.current) setError(e.message || "Error inesperado");
    } finally {
      if (id === revision.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    loadData();
    return () => {
      ++revision.current;
    };
  }, [loadData]);
  const filtered = useMemo(() => filterConcesiones(concesiones, search), [concesiones, search]);

  const totalPeajes = useMemo(
    () => concesiones.reduce((acc, c) => acc + estacionesDe(c).length, 0),
    [concesiones]
  );

  const handleEdit = (concesion) => {
    setConcesionSeleccionada(concesion);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setConcesionSeleccionada(null);
  };

  const handleSaveConcesion = async (code, politicaPagoPayload) => {
    try {
      const res = await updatePoliticaPagoService(code, politicaPagoPayload);

      if (res.success) {
        setSnackbar({
          open: true,
          message: `Concesión "${concesionSeleccionada?.rules?.nombre}" actualizada correctamente.`,
          severity: "success",
        });
        // Recargamos la data para reflejar los cambios
        loadData();
      } else {
        setSnackbar({
          open: true,
          message: res.error || "Error al guardar",
          severity: "error",
        });
      }
      return res;
    } catch (e) {
      setSnackbar({
        open: true,
        message: "Error de conexión al guardar",
        severity: "error",
      });
      return { success: false, error: "Error de conexión al guardar" };
    }
  };

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <MDBox pt={1} pb={3} px={0}>
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          <ConfigPeajesHeader
            totalConcesiones={concesiones.length}
            totalPeajes={totalPeajes}
            loading={loading}
          />

          {/* BARRA BÚSQUEDA */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "#fafbff",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1.5}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <MDTypography variant="subtitle1" fontWeight="bold" sx={{ color: "#1e293b" }}>
                  Matriz por Concesión
                </MDTypography>
                <Chip
                  size="small"
                  label={`${filtered.length} de ${concesiones.length}`}
                  sx={{ bgcolor: "#e2e8f0", color: "#1e293b", fontWeight: 600 }}
                />
              </Box>

              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                <TextField
                  size="small"
                  label="Buscar concesión, código o peaje"
                  placeholder="Nombre, código o modalidad…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{
                    width: { xs: "100%", sm: 340 },
                    bgcolor: "#fff",
                    "& .MuiInputBase-root": { height: "38px" },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Limpiar búsqueda"
                          size="small"
                          onClick={() => setSearch("")}
                        >
                          <ClearIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  aria-label="Actualizar concesiones"
                  disabled={loading || openModal}
                  onClick={loadData}
                  variant="contained"
                  sx={{
                    minWidth: 38,
                    width: 38,
                    height: 38,
                    p: 0,
                    bgcolor: "#164b65",
                    color: "#fff",
                    borderRadius: "8px",
                    "&:hover": { bgcolor: "#103b50" },
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </Button>
              </Box>
            </Box>
          </Box>

          {/* CONTENIDO */}
          <Box sx={{ p: 3 }}>
            {loading && (
              <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                <CircularProgress size={32} sx={{ color: "#1a237e" }} />
              </Box>
            )}

            {!loading && error && (
              <Alert
                severity="error"
                action={<Button onClick={loadData}>Reintentar</Button>}
                sx={{ mb: 2 }}
              >
                {error}
              </Alert>
            )}

            {!loading && !error && filtered.length === 0 && (
              <Box textAlign="center" py={6}>
                <MDTypography variant="body2" color="text.secondary">
                  {search.trim()
                    ? "No hay concesiones que coincidan con la búsqueda."
                    : "No hay concesiones de tipo PEAJE disponibles."}
                </MDTypography>
              </Box>
            )}

            {!loading && !error && filtered.length > 0 && (
              <Grid container spacing={2.5}>
                {filtered.map((c) => (
                  <Grid item xs={12} sm={6} lg={4} key={c.id ?? c.code}>
                    <ConcesionCard concesion={c} onEdit={handleEdit} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Card>
      </MDBox>

      {/* MODAL DE EDICIÓN */}
      <ConcesionModal
        open={openModal}
        onClose={handleCloseModal}
        concesion={concesionSeleccionada}
        onSave={handleSaveConcesion}
      />
    </DashboardLayout>
  );
};

export default ConfiguracionPeajes;
